import { Card, icons } from '@lifebit-ai/lds'
import React, { useId, useState } from 'react'

import { type RequestActivityEvent, RequestActivityEventType } from 'services/request-activity/types'
import { CardBody, CardHeading } from 'ui/card/card.styles'
import { formatLocalDateTime } from 'utils/date'

import * as styles from './RequestActivity.styles'

export const REQUEST_ACTIVITY_TITLE = 'Review activity'
export const REQUEST_ACTIVITY_TEST_ID = 'request-activity'
export const REQUEST_ACTIVITY_EMPTY_TEST_ID = 'request-activity-empty'
export const REQUEST_ACTIVITY_REVEAL_TEST_ID = 'request-activity-reveal'
export const REQUEST_ACTIVITY_EVENTS_TEST_ID = 'request-activity-events'
export const REQUEST_ACTIVITY_VISIBLE_EVENT_LIMIT = 6

const EVENT_ICON: Record<RequestActivityEventType, JSX.Element> = {
  [RequestActivityEventType.Submitted]: <icons.FileAddSmall />,
  // Person-plus is assignment everywhere; a plain person here meant the icon read as the
  // requester, which is what it stands for in the page header a few pixels away.
  [RequestActivityEventType.Assigned]: <icons.AddPersonSmall />,
  [RequestActivityEventType.PeerReviewed]: <icons.UserGroupSmall />,
  [RequestActivityEventType.Unassigned]: <icons.CloseSmall />,
  [RequestActivityEventType.LabelAdded]: <icons.TagSmall />,
  [RequestActivityEventType.LabelRemoved]: <icons.TagSmall />,
  [RequestActivityEventType.Approved]: <icons.CheckInCircleSmall />,
  [RequestActivityEventType.Rejected]: <icons.CloseInCircleSmall />,
  [RequestActivityEventType.AppealSubmitted]: <icons.FileAddSmall />,
  [RequestActivityEventType.AttestationRequested]: <icons.FileAddSmall />,
  [RequestActivityEventType.AttestationSubmitted]: <icons.FileAddSmall />,
  [RequestActivityEventType.DataAccessShared]: <icons.CheckInCircleSmall />
}

export interface RequestActivityProps {
  events: RequestActivityEvent[]
}

const getRevealControlLabel = (hiddenEventCount: number, areOlderEventsRevealed: boolean): string => {
  if (areOlderEventsRevealed) {
    return 'Show less'
  }

  return hiddenEventCount === 1 ? 'Show 1 older event' : `Show ${hiddenEventCount} older events`
}

/**
 * Chronological log of everything that has happened to a request: submission, labelling,
 * assignment and the approve/reject decision. It is not limited to peer review, though the
 * peer-review workflow is what makes it worth showing.
 */
export const RequestActivity = ({ events }: RequestActivityProps): JSX.Element => {
  const [areOlderEventsRevealed, setAreOlderEventsRevealed] = useState(false)
  const eventsId = useId()
  const hiddenEventCount = Math.max(0, events.length - REQUEST_ACTIVITY_VISIBLE_EVENT_LIMIT)
  const hasHiddenEvents = hiddenEventCount > 0
  const visibleEvents =
    hasHiddenEvents && !areOlderEventsRevealed ? events.slice(0, REQUEST_ACTIVITY_VISIBLE_EVENT_LIMIT) : events

  return (
    <Card data-testid={REQUEST_ACTIVITY_TEST_ID}>
      <CardBody>
        <styles.Header>
          <CardHeading>{REQUEST_ACTIVITY_TITLE}</CardHeading>
          {events.length > 0 && (
            <styles.Count>{events.length === 1 ? '1 event' : `${events.length} events`}</styles.Count>
          )}
        </styles.Header>

        {events.length === 0 ? (
          <styles.EmptyMessage data-testid={REQUEST_ACTIVITY_EMPTY_TEST_ID}>
            No activity recorded for this request yet.
          </styles.EmptyMessage>
        ) : (
          <>
            <styles.Events id={eventsId} data-testid={REQUEST_ACTIVITY_EVENTS_TEST_ID}>
              {visibleEvents.map((event, index) => (
                <styles.Event key={event.id}>
                  <styles.Rail aria-hidden>
                    <styles.Icon>{EVENT_ICON[event.type]}</styles.Icon>
                    {index < visibleEvents.length - 1 && <styles.Connector />}
                  </styles.Rail>
                  <styles.Content>
                    <styles.Heading>
                      <styles.EventTitle>{event.title}</styles.EventTitle>
                      {event.at && <styles.Timestamp>{formatLocalDateTime(event.at)}</styles.Timestamp>}
                    </styles.Heading>
                    {event.note && <styles.Note>{event.note}</styles.Note>}
                  </styles.Content>
                </styles.Event>
              ))}
            </styles.Events>
            {hasHiddenEvents && (
              <styles.RevealButton
                htmlType="button"
                data-testid={REQUEST_ACTIVITY_REVEAL_TEST_ID}
                aria-expanded={areOlderEventsRevealed}
                aria-controls={eventsId}
                onClick={() => setAreOlderEventsRevealed(current => !current)}
              >
                <span>{getRevealControlLabel(hiddenEventCount, areOlderEventsRevealed)}</span>
                {areOlderEventsRevealed ? <icons.ChevronUpSmall /> : <icons.ChevronDownSmall />}
              </styles.RevealButton>
            )}
          </>
        )}
      </CardBody>
    </Card>
  )
}

export default RequestActivity
